import { Image } from "@/components/Image";
import type { FoodAndDrinkSectionType } from "@/lib/requests/types";
import type { ViatorProductType } from "@/lib/schemas/viator";
import { cn } from "@/lib/utils";
import {
  icons,
  LucideArrowRight,
  LucideArrowLeft,
  StarIcon,
} from "lucide-react";
import type { ComponentProps } from "react";
import { useRef, useState, useEffect } from "react";
import { SimpleSectionTitle } from "./SimpleSectionTitle";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { ViatorAd } from "./ViatorAd";
import { MoreAboutDestinationButton } from "./LocationRecipe";
import { toSlug } from "@/lib/utils/slugify";

export type ActionType = {
  label: string;
  href: string;
};

export const Section = ({
  name,
  title,
  subtitle,
  text,
  className,
  action,
}: {
  name?: React.ReactNode;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  text: React.ReactNode;
  action?: ActionType;
  className?: string;
}) => (
  <section className={cn("pt-4 rounded-lg md:flex gap-6", className)}>
    <div className="flex-1 pr-6 flex items-center">
      {name && <p className="md:text-lg">{name}</p>}
      {title && <SimpleSectionTitle title={title} subtitle={subtitle} />}
    </div>
    <Card className="flex-[2]">
      <CardContent>
        <div className="mt-3 md:mt-14 max-w-screen-sm md:text-lg leading-normal">
          {text}
        </div>
        {action && (
          <Button asChild className="mt-8" variant="secondary">
            <a href={action.href}>{action.label}</a>
          </Button>
        )}
      </CardContent>
    </Card>
  </section>
);

export const SectionTitle = ({
  children: title,
}: {
  children: React.ReactNode;
}) => (
  <h2 className="text-2xl md:text-4xl font-bold pr-6 md:leading-tight">
    {title}
  </h2>
);

export const SectionTitle2 = ({
  children: title,
}: {
  children: React.ReactNode;
}) => (
  <h3 className="mt-9 text-[26px] font-extralight leading-[1.7]">{title}</h3>
);

export const SectionItems = ({
  children: items,
}: {
  children: FoodAndDrinkSectionType["localDishes"];
}) => (
  <ul className="list-disc py-3 md:px-3.5 grid gap-8">
    {items.map(({ name, description }, index) => (
      <li key={index} className="first-letter:capitalize grid">
        <div className="uppercase text-primary">{name}</div>
        <div className="text-foreground/90 text-lg font-light mt-1">
          {description}
        </div>
      </li>
    ))}
  </ul>
);

export function splitProducts<T>(products: Array<T>, maxPerArray: number) {
  const numSplitArrays = Math.ceil(products.length / maxPerArray);
  const splitArrays = [];

  for (let i = 0; i < numSplitArrays; i++) {
    const startIndex = i * maxPerArray;
    const endIndex = startIndex + maxPerArray;
    const splitArray = products.slice(startIndex, endIndex);
    splitArrays.push(splitArray);
  }

  return splitArrays;
}

export const DayPlanSection = ({
  morning,
  afternoon,
  evening,
  className,
  ...sectionProps
}: {
  morning: string;
  afternoon: string;
  evening: string;
  className?: string;
} & Pick<ComponentProps<typeof Section>, "title" | "name" | "action">) => (
  <Section
    className={cn("mt-24 sm:mt-36", className)}
    text={
      <>
        <strong>Morning</strong>: {morning}
        <br />
        <br />
        <strong>Afternoon</strong>: {afternoon}
        <br />
        <br />
        <strong>Evening</strong>: {evening}
      </>
    }
    {...sectionProps}
  />
);

export const ImageViatorSection = ({
  title,
  subtitle,
  ads,
  className,
  id,
  noH2,
  isLoading,
}: {
  title: string;
  subtitle: string;
  className?: string;
  ads?: ViatorProductType[];
  id?: string;
  noH2?: boolean;
  isLoading?: boolean;
}) => {
  if (!ads?.length) {
    return null;
  }
  return (
    <section
      className={cn(
        "mt-20 md:mt-28 lg:mt-32 xl:mt-36 2xl:mt-44 relative",
        className,
      )}
    >
      <div id={id} className="absolute -top-24 sm:-top-32" />
      <SimpleSectionTitle title={title} subtitle={subtitle} noH2={noH2} />
      <ViatorSection className="mt-11" ads={ads} isLoading={isLoading} />
    </section>
  );
};

export const ViatorSection = ({
  ads,
  className,
  loading,
  isLoading,
}: {
  ads: ViatorProductType[];
  className?: string;
  loading?: ComponentProps<"img">["loading"];
  isLoading?: boolean;
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScroll = () => {
    if (!scrollRef.current) return;

    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
  };

  useEffect(() => {
    checkScroll();
    const scrollElement = scrollRef.current;
    if (scrollElement) {
      scrollElement.addEventListener("scroll", checkScroll);
      return () => scrollElement.removeEventListener("scroll", checkScroll);
    }
  }, [ads]);

  const scroll = (direction: "left" | "right") => {
    if (!scrollRef.current) return;

    const scrollAmount = scrollRef.current.clientWidth * 0.8;
    const newScrollLeft =
      scrollRef.current.scrollLeft +
      (direction === "left" ? -scrollAmount : scrollAmount);

    scrollRef.current.scrollTo({
      left: newScrollLeft,
      behavior: "smooth",
    });
  };

  return (
    <div className={cn("max-w-screen-xl mx-auto relative", className)}>
      <div
        ref={scrollRef}
        className="flex gap-[1px] overflow-x-scroll scrollbar-hidden scroll-smooth"
      >
        {isLoading
          ? [1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <div
                key={i}
                className="flex-shrink-0 h-80 sm:h-[27rem] aspect-[3/4] bg-foreground/10 animate-pulse"
              />
            ))
          : ads.map((product) => (
              <ViatorAd
                className="flex-shrink-0 animate-fade-in aspect-[3/4]"
                key={product.productCode}
                product={product}
                loading={loading}
              />
            ))}
      </div>

      {/* Left Arrow */}
      {canScrollLeft && (
        <Button
          variant="shadow"
          size="wrapper"
          onClick={() => scroll("left")}
          className="bg-background w-10 h-10 sm:w-12 sm:h-12 rounded-full border border-foreground/20 shadow-lg absolute top-1/2 -translate-y-1/2 -left-3 sm:-left-6 z-40 flex items-center justify-center hover:shadow-xl transition-all opacity-100"
          aria-label="Scroll left"
        >
          <LucideArrowLeft className="h-4 w-4 sm:h-5 sm:w-5" />
        </Button>
      )}

      {/* Right Arrow */}
      {canScrollRight && (
        <Button
          variant="shadow"
          size="wrapper"
          onClick={() => scroll("right")}
          className="bg-background w-10 h-10 sm:w-12 sm:h-12 rounded-full border border-foreground/20 shadow-lg absolute top-1/2 -translate-y-1/2 -right-3 sm:-right-6 z-40 flex items-center justify-center hover:shadow-xl transition-all opacity-100"
          aria-label="Scroll right"
        >
          <LucideArrowRight className="h-4 w-4 sm:h-5 sm:w-5" />
        </Button>
      )}
    </div>
  );
};

export const ViatorImageCard = ({
  title,
  imageUrl,
  className,
  product,
  loading,
}: {
  title: string;
  imageUrl: string;
  className?: string;
  product: ViatorProductType;
  loading?: ComponentProps<"img">["loading"];
}) => (
  <a
    href={`/products/${product.productCode}`}
    className={cn(
      "relative overflow-hidden block h-80 sm:h-[27rem] group",
      className,
    )}
    rel="noopener nofollow sponsored"
  >
    <Image
      src={imageUrl}
      style={{
        margin: 0,
      }}
      className="absolute inset-0 w-full h-full object-cover transition-all brightness-90 group-hover:brightness-100 group-hover:scale-[101%] duration-300"
      alt={title}
      loading={loading}
    />
    {product.reviews && (
      <div className="absolute top-4 left-4">
        <div className="text-xs inline-flex gap-1 items-center bg-foreground/40 text-background/90 font-light px-3 py-1 rounded-full">
          <span>{product.reviews.combinedAverageRating.toPrecision(2)}</span>
          <StarIcon
            strokeWidth={1}
            className="h-[18px] w-[18px] fill-amber-300 stroke-none"
          />
          <span>({product.reviews.totalReviews})</span>
        </div>
      </div>
    )}
    <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black" />
    <p className="absolute inset-x-0 bottom-0 p-4 text-background md:text-lg font-medium drop-shadow-md tracking-wide text-start">
      {title}
    </p>
  </a>
);

export const ViatorGrid = ({ products }: { products: ViatorProductType[] }) => (
  <div className="grid xs:grid-cols-[repeat(auto-fill,minmax(240px,1fr))] lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-[1px]">
    {products.map((product) => (
      <ViatorAd key={product.productCode} product={product} />
    ))}
  </div>
);

export const DiscoverDestinationSection = ({
  country,
  city,
  dark,
  className,
}: {
  country: string;
  city?: string;
  dark?: boolean;
  className?: string;
}) => {
  const destination = city || country;
  return (
    <section
      className={cn(
        "py-20 md:py-28 lg:py-32 xl:py-36 2xl:py-48",
        dark && "bg-foreground text-neutral-100",
        className,
      )}
    >
      <SimpleSectionTitle
        dark={dark}
        title={`Discover ${destination}`}
        noH2
        subtitle={
          <>
            Read more in our{" "}
            <a
              className={cn(
                "underline underline-offset-4 transition-colors",
                dark && "hover:text-white",
              )}
              href={
                city
                  ? `/${toSlug(country)}/${toSlug(city)}`
                  : `/${toSlug(country)}`
              }
            >
              {destination} 2025 Travel Guide
            </a>
            .
          </>
        }
      />
      <MoreAboutDestinationButton
        className="mt-10"
        country={country}
        city={city}
      />
    </section>
  );
};

export interface LinkType extends ComponentProps<"a"> {
  label: string;
  iconName: keyof typeof icons;
  isRandomLink?: boolean;
}
